import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  closeCartSidebar,
  fetchCart,
  updateCartQty,
  removeCartItem,
} from "../../data/CartSlice"
import DeleteIcon from "../../icons/DeleteIcon"
import FlashOnIcon from "../../icons/FlashOnIcon"
import styles from "./CartSidebar.module.css"

const CartSidebar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { items: cart, isSidebarOpen, loading } = useSelector((state) => state.cart)
  const { isLoggedIn } = useSelector((state) => state.auth)

  useEffect(() => {
    if (isLoggedIn && isSidebarOpen) {
      dispatch(fetchCart())
    }
  }, [isLoggedIn, isSidebarOpen, dispatch])

  if (!isSidebarOpen) return null

  const handleCheckout = () => {
    dispatch(closeCartSidebar())
    navigate("/order-summary/cart")
  }

  const handleBuySingle = (productId) => {
    dispatch(closeCartSidebar())
    navigate(`/order-summary/${productId}`)
  }

  const totalPrice = cart
    .reduce((total, cartItem) => {
      if (!cartItem?.Product) return total
      const rate = parseFloat(cartItem.Product.rate || 0)
      const qty = parseFloat(cartItem.qty || 1)
      const gst = parseFloat(cartItem.Product.product_category?.gst || 0)
      return total + rate * qty + (rate * qty * gst) / 100
    }, 0)
    .toFixed(2)

  return (
    <>
      {/* Overlay */}
      <div
        className={styles.overlay}
        onClick={() => dispatch(closeCartSidebar())}
      />

      {/* Sidebar Panel */}
      <div className={styles.sidebar}>
        <div className={styles.header}>
          <h2>
            Your Cart (
            {cart.reduce(
              (acc, item) => acc + (parseInt(item.qty, 10) || 0),
              0
            )}
            )
          </h2>
          <button
            className={styles.closeBtn}
            onClick={() => dispatch(closeCartSidebar())}
          >
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {loading && cart.length === 0 ? (
            <p className={styles.loadingText}>Loading cart...</p>
          ) : cart.length === 0 ? (
            <div className={styles.emptyCart}>
              <p>Your cart is empty!</p>
              <button
                onClick={() => {
                  dispatch(closeCartSidebar())
                  navigate("/")
                }}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            cart.map((cartItem) => {
              if (!cartItem?.Product) return null
              const product = cartItem.Product
              const itemTotal = (
                parseFloat(product.rate) * parseFloat(cartItem.qty) +
                (parseFloat(product.rate) *
                  parseFloat(cartItem.qty) *
                  parseFloat(product.product_category?.gst || 0)) /
                  100
              ).toFixed(2)

              return (
                <div key={cartItem.id} className={styles.cartItem}>
                  <img
                    src={`${import.meta.env.VITE_BACKEND_URL}/public/images/products/${
                      product.product_images?.[0]?.image_url
                    }`}
                    alt={product.name}
                  />

                  <div className={styles.itemDetails}>
                    <h4>{product.name}</h4>
                    <p className={styles.price}>₹ {itemTotal}</p>

                    <div className={styles.controls}>
                      <div className={styles.qtyContainer}>
                        <button
                          onClick={() => {
                            const currentQty = parseInt(cartItem.qty, 10) || 1
                            dispatch(
                              updateCartQty({
                                cartItemId: cartItem.id,
                                qty: currentQty - 1,
                              })
                            )
                          }}
                          disabled={parseInt(cartItem.qty, 10) <= 1}
                        >
                          -
                        </button>
                        <span>{cartItem.qty}</span>
                        <button
                          onClick={() => {
                            const currentQty = parseInt(cartItem.qty, 10) || 1
                            dispatch(
                              updateCartQty({
                                cartItemId: cartItem.id,
                                qty: currentQty + 1,
                              })
                            )
                          }}
                        >
                          +
                        </button>
                      </div>

                      <button
                        className={styles.deleteBtn}
                        onClick={() => dispatch(removeCartItem(cartItem.id))}
                        title="Remove item"
                      >
                        <DeleteIcon />
                      </button>
                    </div>

                    {cartItem.qty > product.stock && (
                      <p className={styles.stockError}>
                        Only {product.stock} in stock
                      </p>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {cart.length > 0 && (
          <div className={styles.footer}>
            <div className={styles.totalRow}>
              <span>Subtotal:</span>
              <strong>₹ {totalPrice}</strong>
            </div>

            <div className={styles.actionButtons}>
              <button className={styles.checkoutBtn} onClick={handleCheckout}>
                Proceed to Checkout <FlashOnIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CartSidebar
